import { SEOContentFAQ } from "../../../../components/seo/SEOContentFAQ";
import { SEOContentHowTo } from "../../../../components/seo/SEOContentHowTo";

export const TeleprompterCapCutES = () => (
    <>
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Teleprompter para CapCut: Graba Contenido Profesional en Minutos
        </h2>

        <p className="lead text-xl text-slate-300 mb-8 font-medium">
            Domina el arte de grabar videos impecables para CapCut. <strong>Elimina errores de grabación y mantén un contacto visual perfecto</strong> con el teleprompter inteligente de PromptNinja.
        </p>

        <p className="text-slate-300 mb-8 leading-relaxed">
            En esta guía esencial para creadores, mostramos por qué <strong>PromptNinja</strong> es el socio indispensable para quienes editan en CapCut. Aprende a grabar material crudo de alta calidad en la primera toma, ahorrando horas preciosas de edición y permitiéndote concentrarte en lo que realmente importa: la creatividad y los efectos virales de CapCut. Descubre cómo nuestra herramienta online gratuita transforma tu smartphone o computadora en un estudio profesional, asegurando fluidez, confianza y autoridad en cada video.
        </p>

        <div className="bg-slate-900/50 p-6 md:p-8 rounded-2xl border border-slate-800 shadow-xl mb-12">
            <h3 className="text-2xl font-bold text-white mb-6">¿Por qué Usar PromptNinja Antes de Editar en CapCut?</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <li className="flex items-start gap-3 bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                    <span className="text-blue-400 text-xl">💎</span>
                    <div className="text-sm">
                        <strong className="text-white block mb-1">Materia Prima de Calidad</strong>
                        <span className="text-slate-400">Graba videos fluidos y sin errores. Esto facilita enormemente la aplicación de efectos y transiciones en CapCut.</span>
                    </div>
                </li>
                <li className="flex items-start gap-3 bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                    <span className="text-blue-400 text-xl">🎨</span>
                    <div className="text-sm">
                        <strong className="text-white block mb-1">Foco en la Edición Creativa</strong>
                        <span className="text-slate-400">En lugar de perder tiempo cortando tropiezos, usa tu tiempo en CapCut para agregar subtítulos y efectos increíbles.</span>
                    </div>
                </li>
                <li className="flex items-start gap-3 bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                    <span className="text-blue-400 text-xl">👁️</span>
                    <div className="text-sm">
                        <strong className="text-white block mb-1">Contacto Visual Mantenido</strong>
                        <span className="text-slate-400">El teleprompter te permite mirar directamente a la lente mientras lees, haciendo tu video mucho más profesional.</span>
                    </div>
                </li>
                <li className="flex items-start gap-3 bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                    <span className="text-blue-400 text-xl">⚡</span>
                    <div className="text-sm">
                        <strong className="text-white block mb-1">Agilidad en la Producción</strong>
                        <span className="text-slate-400">Graba más rápido, edita más rápido y publica con mucha más frecuencia. PromptNinja acelera todo tu proceso.</span>
                    </div>
                </li>
            </ul>
        </div>

        <div className="bg-gradient-to-r from-red-900/30 to-orange-900/30 p-8 rounded-xl border border-red-500/30 my-12">
            <h3 className="text-3xl font-bold text-white mb-6">El Ciclo Vicioso del Creador de Contenido</h3>
            <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-slate-900/50 p-6 rounded-lg border border-red-500/30">
                    <h3 className="font-bold text-red-400 mb-4 flex items-center gap-2">
                        <span>❌</span> SIN Teleprompter
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>🎬 <strong>Grabación:</strong> 2h para grabar 3min de video (20+ tomas)</li>
                        <li>📂 <strong>Crudo:</strong> 18min de archivos llenos de errores</li>
                        <li>✂️ <strong>Edición en CapCut:</strong> 3h cortando errores, sincronizando cortes</li>
                        <li>😫 <strong>Creatividad:</strong> Cero. Solo quieres terminar</li>
                        <li>📊 <strong>Resultado:</strong> Video "ok", publicado sin cariño</li>
                        <li>⏰ <strong>Tiempo total:</strong> 5h+ por video</li>
                    </ul>
                </div>
                <div className="bg-slate-900/50 p-6 rounded-lg border border-green-500/30">
                    <h3 className="font-bold text-green-400 mb-4 flex items-center gap-2">
                        <span>✅</span> CON PromptNinja
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>🎬 <strong>Grabación:</strong> 20min para grabar 3min (1-2 tomas)</li>
                        <li>📂 <strong>Crudo:</strong> 4min de archivos limpios</li>
                        <li>✂️ <strong>Edición en CapCut:</strong> 1h creando efectos, subtítulos, transiciones</li>
                        <li>😊 <strong>Creatividad:</strong> Alta. Foco en lo que importa</li>
                        <li>📊 <strong>Resultado:</strong> Video profesional con tu marca</li>
                        <li>⏰ <strong>Tiempo total:</strong> 1h30min por video</li>
                    </ul>
                </div>
            </div>
            <p className="text-slate-400 text-sm mt-6 italic text-center">
                💡 Ahorro: <strong className="text-green-400">3h40min por video</strong>. ¡Si haces 4 videos/mes, son <strong className="text-green-400">14 horas ahorradas</strong>!
            </p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">¿Por qué el Teleprompter Integrado de CapCut No es Suficiente?</h3>
        <p className="mb-6">
            CapCut tiene un teleprompter nativo en la función de grabación. Suena práctico, pero tiene limitaciones críticas que impiden grabaciones profesionales:
        </p>

        <div className="overflow-x-auto my-8">
            <table className="w-full text-left border-collapse bg-slate-800 rounded-lg">
                <thead>
                    <tr className="bg-slate-700">
                        <th className="p-4 border border-slate-600">Característica</th>
                        <th className="p-4 border border-slate-600 text-center">PromptNinja</th>
                        <th className="p-4 border border-slate-600 text-center">Teleprompter CapCut</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className="p-4 border border-slate-700 font-semibold">Control Remoto</td>
                        <td className="p-4 border border-slate-700 text-center text-green-400">✔ Vía Código QR (otro teléfono)</td>
                        <td className="p-4 border border-slate-700 text-center text-red-400">✖ Toque en pantalla (imposible al grabar)</td>
                    </tr>
                    <tr>
                        <td className="p-4 border border-slate-700 font-semibold">Ajuste de Velocidad Real</td>
                        <td className="p-4 border border-slate-700 text-center text-green-400">✔ Ajuste instantáneo vía remoto</td>
                        <td className="p-4 border border-slate-700 text-center text-yellow-400">~ Necesita pausar grabación</td>
                    </tr>
                    <tr>
                        <td className="p-4 border border-slate-700 font-semibold">Control por Voz (IA)</td>
                        <td className="p-4 border border-slate-700 text-center text-green-400">✔ Texto se mueve al hablar</td>
                        <td className="p-4 border border-slate-700 text-center text-red-400">✖ No disponible</td>
                    </tr>
                    <tr>
                        <td className="p-4 border border-slate-700 font-semibold">Funciona con Cualquier Cámara</td>
                        <td className="p-4 border border-slate-700 text-center text-green-400">✔ Corre separado (overlay)</td>
                        <td className="p-4 border border-slate-700 text-center text-red-400">✖ Solo dentro de CapCut</td>
                    </tr>
                    <tr>
                        <td className="p-4 border border-slate-700 font-semibold">Tamaño de Fuente</td>
                        <td className="p-4 border border-slate-700 text-center text-green-400">Personalizable (8px-200px)</td>
                        <td className="p-4 border border-slate-700 text-center text-yellow-400">Limitado (3 tamaños fijos)</td>
                    </tr>
                    <tr>
                        <td className="p-4 border border-slate-700 font-semibold">Funciona Offline</td>
                        <td className="p-4 border border-slate-700 text-center text-green-400">✔ PWA Instalable</td>
                        <td className="p-4 border border-slate-700 text-center text-yellow-400">~ Depende de actualización de CapCut</td>
                    </tr>
                    <tr>
                        <td className="p-4 border border-slate-700 font-semibold">Precio</td>
                        <td className="p-4 border border-slate-700 text-center text-green-400">Gratis (remoto incluido)</td>
                        <td className="p-4 border border-slate-700 text-center text-green-400">Gratis</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <p className="text-slate-400 text-sm italic mb-12">
            💡 <strong>Consejo Pro:</strong> Usa PromptNinja para grabar, pero aprovecha la app de cámara nativa de tu teléfono en 4K (60fps). Luego importa a CapCut con la mejor calidad posible.
        </p>

        <div className="bg-slate-800 p-6 rounded-lg my-12 border-l-4 border-cyan-500">
            <h3 className="text-2xl font-bold text-white mb-4">Flujo de Trabajo Profesional: PromptNinja → CapCut</h3>
            <p className="text-slate-300 mb-4">
                El combo perfecto no es elegir uno u otro. Es usar cada herramienta para lo que hace mejor:
            </p>
            <ul className="space-y-3 text-slate-300">
                <li><strong className="text-cyan-400">PromptNinja:</strong> Graba material 100% limpio sin errores de texto</li>
                <li><strong className="text-cyan-400">CapCut:</strong> Agrega subtítulos auto, transiciones virales, efectos tendencia, música</li>
            </ul>
        </div>

        <SEOContentHowTo
            title="Paso a Paso: Grabación Perfecta para CapCut"
            schemaTitle="Cómo Usar Teleprompter con CapCut"
            totalTime="PT15M"
            tools={["PromptNinja", "Smartphone", "CapCut"]}
            steps={[
                {
                    title: "Paso 1: Escribe el Guion en PromptNinja",
                    text: "Ve a promptninja.solutionkit.com.br y pega tu texto. Ajusta el tamaño de fuente para leer cómodamente a 1 metro. Usa MAYÚSCULAS o colores para marcar palabras clave a enfatizar."
                },
                {
                    title: "Paso 2: Configura el Control Remoto (Opcional pero Recomendado)",
                    text: "Haz clic en 'Conectar Dispositivo' y escanea el código QR con un segundo teléfono o tablet. Esto te permite pausar/reanudar/ajustar velocidad sin tocar el teléfono de grabación."
                },
                {
                    title: "Paso 3: Posiciona el Teléfono y el Teleprompter",
                    text: "Coloca el teléfono de grabación en un trípode a la altura de los ojos. Abre PromptNinja en otro dispositivo (o el mismo usando picture-in-picture si está disponible) y posiciona justo debajo o al lado de la cámara."
                },
                {
                    title: "Paso 4: Graba con Foco Total",
                    text: "Presiona REC en tu app de cámara nativa (no en CapCut todavía). Lee el texto naturalmente, pausando cuando sea necesario con el control remoto. Sin prisa. Una toma perfecta vence a 10 mediocres."
                },
                {
                    title: "Paso 5: Importa a CapCut",
                    text: "Abre CapCut, crea un nuevo proyecto e importa el video de la galería. Como no te equivocaste en el texto, tu línea de tiempo estará limpia. Ahora solo agrega la magia: Subtítulos Auto, efectos de zoom, música de fondo."
                },
                {
                    title: "Paso 6: Subtítulos Auto de Alta Precisión",
                    text: "Usa 'Texto → Subtítulos automáticos'. Como tu dicción fue guiada por el guion, CapCut generará subtítulos con 95%+ de precisión. Corrige las pocas palabras erradas y aplica animaciones virales."
                }
            ]}
        />

        <h3 className="text-3xl font-bold text-white mt-16 mb-6">Caso Real: TikToker Pasó de 8h a 3h/Semana Grabando</h3>
        <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 p-8 rounded-xl border border-purple-500/30 mb-12">
            <p className="text-slate-300 mb-4">
                <strong className="text-purple-400">Perfil:</strong> Creadora de contenido educativo en TikTok (@study_with_emma), 87k seguidores, publica 5 videos/semana sobre tips de estudio.
            </p>
            <p className="text-slate-300 mb-6">
                <strong className="text-purple-400">Problema:</strong> Gastaba 8h cada lunes grabando los 5 videos de la semana. Como memorizaba, se tropezaba seguido, y cada video de 60s tomaba 1h30min para quedar 'aceptable'. Resultado: burnout, videos con baja energía en las últimas tomas.
            </p>

            <h3 className="font-bold text-white mb-3">Cambios Implementados:</h3>
            <ol className="list-decimal pl-6 space-y-2 text-slate-300 mb-6">
                <li>Empezó a escribir guiones detallados el domingo (30min para 5 videos)</li>
                <li>Lunes: abre PromptNinja en tablet, coloca detrás del teléfono</li>
                <li>Graba los 5 videos en 2h (cada uno toma 20-25min, 2 tomas max)</li>
                <li>Edita en CapCut: 1h total (12min por video, enfocándose en subtítulos y efectos)</li>
            </ol>

            <div className="bg-slate-900/50 p-6 rounded-lg">
                <h3 className="font-bold text-green-400 mb-3">Resultados en 2 Meses:</h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                    <li>⏱️ Tiempo de producción: <strong>-62%</strong> (de 8h a 3h/semana)</li>
                    <li>📈 Engagement promedio: <strong>+34%</strong> (videos más fluidos, menos errores visibles)</li>
                    <li>💪 Energía mental: Creatividad libre para probar nuevos formatos</li>
                    <li>🎬 Calidad: Videos se ven "más profesionales" por comentarios</li>
                </ul>
            </div>

            <p className="text-slate-400 text-xs italic mt-4">
                *Datos compartidos con permiso. Los resultados pueden variar según tipo de contenido y experiencia.
            </p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">Consejos Avanzados para Usuarios de CapCut + PromptNinja</h3>

        <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-cyan-400 mb-3 text-xl">📱 Graba en Retrato 9:16</h3>
                <p className="text-slate-300 text-sm mb-3">
                    Si tu contenido es para TikTok/Reels/Shorts, pon PromptNinja en modo retrato (vertical). Ajusta el ancho del texto para que quepa en pantalla sin scroll horizontal.
                </p>
                <p className="text-xs text-slate-500">
                    <strong>Tip Pro:</strong> Solo usa paisaje si es para YouTube horizontal.
                </p>
            </div>

            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-cyan-400 mb-3 text-xl">🎨 Marca Puntos de Corte en Guion</h3>
                <p className="text-slate-300 text-sm mb-3">
                    Usa [CORTE] o *** en el guion para recordar dónde agregarás transiciones o b-roll en CapCut. Facilita la edición.
                </p>
                <p className="text-xs text-slate-500">
                    <strong>Ejemplo:</strong> "Aquí hay 3 consejos [CORTE] Primero: siempre planea..."
                </p>
            </div>

            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-cyan-400 mb-3 text-xl">⚡ Usa la Función [CUENTA 3]</h3>
                <p className="text-slate-300 text-sm mb-3">
                    En PromptNinja Pro, puedes insertar comandos como [CUENTA 3] para una cuenta regresiva hablada. Perfecto para crear suspenso antes de revelar algo.
                </p>
                <p className="text-xs text-slate-500">
                    <strong>CapCut lo amará:</strong> Fácil de sincronizar zoom o efecto glitch en la cuenta regresiva.
                </p>
            </div>

            <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
                <h3 className="font-bold text-cyan-400 mb-3 text-xl">🎯 Velocidad de 180-200 PPM</h3>
                <p className="text-slate-300 text-sm mb-3">
                    Para videos cortos (TikTok/Shorts), ajusta velocidad entre 180-200 palabras por minuto. Es rápido pero comprensible. Para videos largos YouTube, usa 140-160 PPM.
                </p>
                <p className="text-xs text-slate-500">
                    <strong>Prueba:</strong> Graba 30s y ve si pudiste seguir cómodamente.
                </p>
            </div>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">Errores Comunes al Combinar Teleprompter + CapCut</h3>

        <div className="space-y-4 mb-12">
            <div className="bg-slate-800 p-5 rounded-lg border-l-4 border-red-500">
                <h3 className="font-bold text-white mb-2">❌ Error 1: Grabar Directamente en CapCut</h3>
                <p className="text-slate-300 text-sm mb-2">
                    Mucha gente graba usando la cámara de CapCut porque ya está ahí. Problema: si la app se cierra o sales accidentalmente, pierdes la grabación.
                </p>
                <p className="text-green-400 text-sm">
                    <strong>✅ Solución:</strong> Siempre graba en la app de cámara nativa. Se guarda en galería y tienes backup.
                </p>
            </div>

            <div className="bg-slate-800 p-5 rounded-lg border-l-4 border-red-500">
                <h3 className="font-bold text-white mb-2">❌ Error 2: No Probar Posicionamiento Primero</h3>
                <p className="text-slate-300 text-sm mb-2">
                    Poner el teleprompter muy lejos de la cámara hace que muevas los ojos MUCHO, haciendo obvio que lees.
                </p>
                <p className="text-green-400 text-sm">
                    <strong>✅ Solución:</strong> Pon el dispositivo con PromptNinja <em>justo debajo o al lado</em> de la lente. Idealmente no más de 20cm lejos.
                </p>
            </div>

            <div className="bg-slate-800 p-5 rounded-lg border-l-4 border-red-500">
                <h3 className="font-bold text-white mb-2">❌ Error 3: Fuente Muy Pequeña</h3>
                <p className="text-slate-300 text-sm mb-2">
                    Usar fuente de 18px cuando estás a 1 metro te obliga a entrecerrar ojos o inclinarte, rompiendo naturalidad.
                </p>
                <p className="text-green-400 text-sm">
                    <strong>✅ Solución:</strong> Usa al menos 32-40px. Prueba: debes poder leer SIN esfuerzo. Ajusta según distancia y vista.
                </p>
            </div>

            <div className="bg-slate-800 p-5 rounded-lg border-l-4 border-red-500">
                <h3 className="font-bold text-white mb-2">❌ Error 4: Editar Antes de Grabar Todo</h3>
                <p className="text-slate-300 text-sm mb-2">
                    Grabas un video, lo editas al tiro, luego grabas otro. Pierdes ritmo y cada grabación tiene energía diferente.
                </p>
                <p className="text-green-400 text-sm">
                    <strong>✅ Solución:</strong> Método por lotes: graba TODOS los videos de la semana de una vez (con mismo setup). Luego edita todo de una vez en CapCut.
                </p>
            </div>
        </div>

        <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 p-8 rounded-xl border border-cyan-500/30 my-12">
            <h3 className="text-2xl font-bold text-white mb-4">🔥 Hack de Productividad: Grabación por Lotes</h3>
            <p className="text-slate-300 mb-4">
                Si necesitas producir varios videos, arma el ambiente UNA VEZ y graba todo en secuencia:
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-slate-300 mb-4">
                <li>Prepara 5 guiones en PromptNinja (pega todos en pestañas separadas)</li>
                <li>Arma luz y trípode</li>
                <li>Graba video 1, pausa 30 seg, cambia pestaña, graba video 2...</li>
                <li>En 1 hora grabas material para toda la semana</li>
                <li>Dedica 1 día solo a editar todo en CapCut</li>
            </ol>
            <p className="text-cyan-300 font-semibold">
                Resultado: "Trabajas" 2 días pero liberas toda la semana para otras tareas (miniaturas, copy, analytics).
            </p>
        </div>

        <div className="text-center mt-12 mb-12">
            <a
                href="https://promptninja.solutionkit.com.br?lang=es"
                className="inline-block bg-gradient-to-r from-cyan-500 to-blue-600 font-bold py-4 px-10 rounded-full transition hover:brightness-110 hover:scale-105 transform shadow-lg"
                style={{ color: 'white' }}
            >
                Empieza a Crear Contenido Profesional Ahora (Gratis)
            </a>
            <p className="text-slate-400 mt-4 text-sm">Sin registro. Sin tarjeta. Sin trucos.</p>
        </div>

        <SEOContentFAQ
            title="Preguntas Frecuentes: PromptNinja + CapCut"
            items={[
                {
                    question: "¿PromptNinja exporta directamente a CapCut?",
                    answer: "No directamente. El flujo es: grabas el video usando la cámara nativa de tu teléfono mientras lees el teleprompter. El video se guarda en galería. Luego abres CapCut e importas desde galería. Simple y seguro."
                },
                {
                    question: "¿Puedo usar efectos y transiciones de CapCut normalmente?",
                    answer: "¡Sí! Ese es exactamente el punto: graba el video 'limpio' usando PromptNinja (sin errores de texto), luego agrega toda la magia en CapCut — subtítulos auto, zoom dramático, música viral. Cada herramienta hace lo que mejor sabe."
                },
                {
                    question: "¿La calidad del video es buena?",
                    answer: "La calidad depende de la cámara de tu teléfono, no de PromptNinja. Como grabas usando la app de cámara nativa (no un grabador de navegador), puedes usar máxima resolución: 4K 60fps si tu cel soporta. PromptNinja solo muestra el texto."
                },
                {
                    question: "¿Necesito internet para usarlo?",
                    answer: "Primera vez: sí, para cargar el sitio. Luego puedes instalar como PWA (acceso directo) y funciona 100% offline. El control remoto P2P tampoco necesita internet — solo Wi-Fi local para conectar los dos dispositivos."
                },
                {
                    question: "¿Y si quiero grabar un podcast largo de 30-40 minutos?",
                    answer: "¡Perfecto! Usa la función 'Auto Scroll' con velocidad ajustable, o mejor aún: Control por Voz (Pro). El texto se mueve al hablar, sin tocar nada. Ideal para contenido largo donde necesitas gesticular."
                },
                {
                    question: "¿Funciona en iPhone y Android?",
                    answer: "Sí, ambos. PromptNinja corre 100% en navegador (Safari en iPhone, Chrome en Android). No necesitas bajar nada de App Store. Solo visita el sitio. Incluso puedes instalar como app."
                },
                {
                    question: "¿Hay límite de tiempo de grabación?",
                    answer: "PromptNinja no limita. Solo muestra texto. El límite es tu teléfono: espacio y batería. Tip: si grabas mucho (20min+), deja el cel cargando."
                },
                {
                    question: "¿Los subtítulos automáticos de CapCut mejoran con teleprompter?",
                    answer: "¡SÍ! Mucho mejor. Como hablas siguiendo un guion escrito, tu dicción es más clara sin tartamudeos. El algoritmo de CapCut reconoce palabras con 90-95% de precisión (vs ~70% improvisando). Menos corrección manual = más tiempo para creatividad."
                }
            ]}
        />

        <div className="bg-slate-800 p-8 rounded-lg mt-16 mb-8">
            <h3 className="text-2xl font-bold text-white mb-4 text-center">Recursos Relacionados</h3>
            <div className="grid md:grid-cols-2 gap-4">
                <a href="/es/teleprompter-tiktok-shorts" className="block p-4 bg-slate-900 rounded-lg hover:bg-slate-700 transition border border-slate-700">
                    <span className="text-2xl mb-2 block">📱</span>
                    <h4 className="font-bold text-white mb-1">Teleprompter para TikTok y Shorts</h4>
                    <p className="text-sm text-slate-400">Tips específicos para video vertical corto</p>
                </a>
                <a href="/es/better-teleprompter-app" className="block p-4 bg-slate-900 rounded-lg hover:bg-slate-700 transition border border-slate-700">
                    <span className="text-2xl mb-2 block">⭐</span>
                    <h4 className="font-bold text-white mb-1">Comparativa de Apps</h4>
                    <p className="text-sm text-slate-400">PromptNinja vs competidores pagos</p>
                </a>
                <a href="/es/como-usar-teleprompter" className="block p-4 bg-slate-900 rounded-lg hover:bg-slate-700 transition border border-slate-700">
                    <span className="text-2xl mb-2 block">📖</span>
                    <h4 className="font-bold text-white mb-1">Guía Completa de Uso</h4>
                    <p className="text-sm text-slate-400">Tutorial paso a paso para principiantes</p>
                </a>
                <a href="/es/oratoria-video" className="block p-4 bg-slate-900 rounded-lg hover:bg-slate-700 transition border border-slate-700">
                    <span className="text-2xl mb-2 block">🎤</span>
                    <h4 className="font-bold text-white mb-1">Consejos de Oratoria</h4>
                    <p className="text-sm text-slate-400">Cómo hablar natural leyendo</p>
                </a>
            </div>
        </div>

        <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 p-8 rounded-xl border border-purple-500/30 text-center my-12">
            <h3 className="text-3xl font-bold text-white mb-4">Tu Jornada de Creador Empieza Aquí</h3>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Deja de pelear con las retomas. Deja de gastar 3 horas editando errores. Empieza a crear contenido como siempre soñaste: con foco, calidad y creatividad de sobra.
            </p>
            <a
                href="https://promptninja.solutionkit.com.br?lang=es"
                className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 font-bold py-4 px-10 rounded-full transition hover:brightness-110 hover:scale-105 transform shadow-lg text-lg"
                style={{ color: 'white' }}
            >
                Usar PromptNinja Gratis Ahora →
            </a>
        </div>
    </>
);
